import { useState, useCallback, useEffect, useRef } from 'react';
import { cupsService } from '../services/cupsService';
import { esCorreoValido } from '../utils/emailValidation';
import { FEATURE_NOTIFICACION_CORREO_HABILITADA } from '../featureFlags';

interface ProgresoGestion {
  PorcentajeAprobados: number;
  PorcentajePendientes: number;
  PorcentajeRechazados: number;
  TotalAprobados: number;
  TotalCups: number;
  TotalPendientes: number;
  TotalRechazados: number;
}

interface Servicio {
  CantidadAprobados: number;
  CantidadCups: number;
  CantidadPendientes: number;
  CantidadRechazados: number;
  Encabezado: string | null;
  Id: number;
  Observaciones: string | null;
  Servicio: string;
}

interface EstadoObjetosResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    __type: string;
    EstadoObjetos: Array<{
      IsSuccess: boolean;
      Message: string;
      ObjectId: number;
    }>;
    IdsError: number;
    IdsOk: number;
  };
}

export const useCupsGestion = (
  anexoId?: number,
  onMostrarResultadoGestion?: (
    tipo: 'aprobado' | 'rechazado', 
    porcentaje: number, 
    totalRechazados: number,
    correoDestino?: string,
    estadoNotificacion?: string,
    mensajeNotificacion?: string
  ) => void,
  nombreAnexoCorreo?: string,
  codigoAnexoCorreo?: string,
  onSolicitarCorreoAprobador?: (anexoId: number, continuarConCorreo: (correo: string) => Promise<void>) => void
) => {


  const [servicios, setServicios] = useState<Servicio[]>([]);
  const [progresoGestion, setProgresoGestion] = useState<ProgresoGestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [procesando, setProcesando] = useState(false);
  
  // Estado para tracking de sesión usando useRef para persistir entre renders
  const estadoSesion = useRef({
    pendientesIniciales: null as number | null,
    tuvoOperacionesExitosas: false
  });
  const yaNotificadoRef = useRef(false);
  const cargandoRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset automático y carga inicial cuando cambia anexoId
  useEffect(() => {
    if (!anexoId) return;
    
    console.log('🔄 [Hook] Reset de estado de sesión para anexo:', anexoId);
    
    // Limpiar timeout pendiente si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Reset del estado de sesión
    estadoSesion.current = {
      pendientesIniciales: null,
      tuvoOperacionesExitosas: false
    };
    yaNotificadoRef.current = false;
    cargandoRef.current = false;
  }, [anexoId]);

  // Efecto para cargar servicios inicialmente después del reset
  useEffect(() => {
    if (anexoId) {
      // Cargar datos automáticamente después del reset
      const timer = setTimeout(async () => {
        if (cargandoRef.current) return;
        
        cargandoRef.current = true;
        setLoading(true);
        console.log('📡 [Hook] Obteniendo servicios desde API para anexo:', anexoId);
        
        try {
          const data = await cupsService.obtenerServiciosTarifarios(anexoId);
          
          if (data.ResponseCode === 200) {
            setServicios(data.Result.Servicios);
            setProgresoGestion(data.Result.ProgresoGestion);
            
            // Establecer pendientes iniciales solo en la primera carga
            if (estadoSesion.current.pendientesIniciales === null) {
              const pendientes = data.Result.ProgresoGestion?.TotalPendientes || 0;
              estadoSesion.current.pendientesIniciales = pendientes;
              console.log('📊 [Hook] Estableciendo pendientes iniciales:', pendientes);
            }
            
          } else {
            console.error('Error al cargar servicios:', data.ResponseDetail);
            setServicios([]);
            setProgresoGestion(null);
          }
        } catch (error) {
          console.error('Error al cargar servicios:', error);
          setServicios([]);
          setProgresoGestion(null);
        } finally {
          cargandoRef.current = false;
          setLoading(false);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [anexoId]);

  const cargarServicios = useCallback(async () => {
    if (!anexoId || cargandoRef.current) return;
    
    cargandoRef.current = true;
    setLoading(true);
    console.log('📡 [Hook] Obteniendo servicios desde API para anexo:', anexoId);
    
    try {
      const data = await cupsService.obtenerServiciosTarifarios(anexoId);
      
      if (data.ResponseCode === 200) {
        setServicios(data.Result.Servicios);
        setProgresoGestion(data.Result.ProgresoGestion);
        
        // Establecer pendientes iniciales solo en la primera carga
        if (estadoSesion.current.pendientesIniciales === null) {
          const pendientes = data.Result.ProgresoGestion?.TotalPendientes || 0;
          estadoSesion.current.pendientesIniciales = pendientes;
          console.log('📊 [Hook] Estableciendo pendientes iniciales:', pendientes);
        }
        
      } else {
        console.error('Error al cargar servicios:', data.ResponseDetail);
        setServicios([]);
        setProgresoGestion(null);
      }
    } catch (error) {
      console.error('Error al cargar servicios:', error);
      setServicios([]);
      setProgresoGestion(null);
    } finally {
      cargandoRef.current = false;
      setLoading(false);
    }
  }, [anexoId]);

  // Función para programar recarga diferida (cancela la anterior si existe)
  const programarRecargaDiferida = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      console.log('⏰ [Hook] Cancelando recarga diferida anterior...');
    }
    
    console.log('⏰ [Hook] Programando recarga diferida en 1 segundo...');
    timeoutRef.current = setTimeout(() => {
      console.log('⏰ [Hook] Ejecutando recarga diferida programada...');
      cargarServicios();
    }, 1000);
  }, [cargarServicios]);

  // Función para registrar operación exitosa
  const registrarOperacionExitosa = useCallback(() => {
    estadoSesion.current.tuvoOperacionesExitosas = true;
    console.log('✅ [Hook] Operación exitosa registrada. Estado sesión:', {
      pendientesIniciales: estadoSesion.current.pendientesIniciales,
      tuvoOperacionesExitosas: estadoSesion.current.tuvoOperacionesExitosas
    });
    
    // Programar recarga diferida
    programarRecargaDiferida();
  }, [programarRecargaDiferida]);

  const actualizarEstadoCups = useCallback(async (
    objectIds: number[],
    tipo: 'CUPS' | 'CUPS_SERVICIOS',
    estado: 'SI' | 'NO',
    observaciones?: string
  ): Promise<EstadoObjetosResponse> => {
    setProcesando(true);
    try {
      const response = await cupsService.actualizarEstadoCups(objectIds, tipo, estado, observaciones);

      if (response.ResponseCode === 200 && response.Result?.IdsOk > 0) {
        registrarOperacionExitosa();
      }

      return response;
    } finally {
      setProcesando(false);
    }
  }, [registrarOperacionExitosa]);

  const verificarCupsPendientes = useCallback((totalPendientesModal?: number): number => {
    if (cupsService.esDesarrollo()) {
      return cupsService.verificarCupsPendientes();
    } else {
      return cupsService.verificarCupsPendientesConTotales(totalPendientesModal || 0);
    }
  }, []);

  // Función de validación de respuesta del servicio
  const validarRespuestaServicio = useCallback((
    response: EstadoObjetosResponse, 
    idsEnviados: number[], 
    tipoOperacion: 'CUPS' | 'CUPS_SERVICIOS'
  ): { esExitoso: boolean; mensaje: string } => {
    // Función auxiliar para formatear mensajes con plurales correctos
    const formatearMensaje = (cantidad: number, exitosos: number, fallidos: number, tipo: 'CUPS' | 'CUPS_SERVICIOS') => {
      const esPlural = cantidad !== 1;
      const exitososPlural = exitosos !== 1;
      const fallidosPlural = fallidos !== 1;
      
      if (tipo === 'CUPS_SERVICIOS') {
        const servicioText = esPlural ? 'servicios' : 'servicio';
        const exitososText = exitosos > 0 ? (exitososPlural ? `${exitosos} servicios` : `${exitosos} servicio`) : '';
        const fallidosText = fallidos > 0 ? (fallidosPlural ? `${fallidos} servicios` : `${fallidos} servicio`) : '';
        
        if (exitosos > 0 && fallidos > 0) {
          return `Se ${exitososPlural ? 'procesaron' : 'procesó'} ${exitososText} correctamente, pero ${fallidosPlural ? 'fallaron' : 'falló'} ${fallidosText}`;
        }
        return servicioText;
      } else {
        const cupsText = esPlural ? 'CUPS' : 'CUPS';
        const exitososText = exitosos > 0 ? `${exitosos} CUPS` : '';
        const fallidosText = fallidos > 0 ? `${fallidos} CUPS` : '';
        
        if (exitosos > 0 && fallidos > 0) {
          return `Se ${exitososPlural ? 'procesaron' : 'procesó'} ${exitososText} correctamente, pero ${fallidosPlural ? 'fallaron' : 'falló'} ${fallidosText}`;
        }
        return cupsText;
      }
    };

    // Verificar ResponseCode
    if (response.ResponseCode !== 200) {
      return {
        esExitoso: false,
        mensaje: `Error en el servicio: ${response.ResponseDetail || 'Error desconocido'}`
      };
    }

    // Verificar que la estructura Result existe
    if (!response.Result) {
      return {
        esExitoso: false,
        mensaje: 'Respuesta del servicio incompleta'
      };
    }

    const { IdsOk, IdsError, EstadoObjetos } = response.Result;

    // Verificar si hay errores
    if (IdsError > 0) {
      return {
        esExitoso: false,
        mensaje: formatearMensaje(IdsOk + IdsError, IdsOk, IdsError, tipoOperacion)
      };
    }

    // Para servicios completos (CUPS_SERVICIOS), no validar la cantidad exacta
    if (tipoOperacion === 'CUPS_SERVICIOS') {
      if (IdsOk > 0 && IdsError === 0) {
        return {
          esExitoso: true,
          mensaje: 'Operación completada exitosamente'
        };
      } else {
        const servicioTexto = idsEnviados.length === 1 ? 'servicio' : 'servicios';
        return {
          esExitoso: false,
          mensaje: `No se procesaron ${servicioTexto} o ocurrieron errores`
        };
      }
    }

    // Para CUPS individuales, verificar que todos los IDs enviados fueron procesados
    if (tipoOperacion === 'CUPS') {
      if (IdsOk !== idsEnviados.length) {
        const cupsTexto = idsEnviados.length === 1 ? 'CUPS' : 'CUPS';
        const procesadosTexto = IdsOk === 1 ? 'CUPS' : 'CUPS';
        return {
          esExitoso: false,
          mensaje: `Se esperaba procesar ${idsEnviados.length} ${cupsTexto}, pero solo se ${IdsOk === 1 ? 'procesó' : 'procesaron'} ${IdsOk} ${procesadosTexto}`
        };
      }

      // Verificar que los IDs en EstadoObjetos corresponden con los enviados y son exitosos
      if (EstadoObjetos && EstadoObjetos.length > 0) {
        for (const idEnviado of idsEnviados) {
          const objetoEncontrado = EstadoObjetos.find(obj => obj.ObjectId === idEnviado);
          
          if (!objetoEncontrado) {
            return {
              esExitoso: false,
              mensaje: `El ID ${idEnviado} no fue encontrado en la respuesta`
            };
          }
          
          if (!objetoEncontrado.IsSuccess) {
            return {
              esExitoso: false,
              mensaje: `El ID ${idEnviado} falló: ${objetoEncontrado.Message || 'Error desconocido'}`
            };
          }
        }
      } else if (idsEnviados.length > 0) {
        return {
          esExitoso: false,
          mensaje: 'No se recibieron detalles de los objetos procesados'
        };
      }
    }

    return {
      esExitoso: true,
      mensaje: 'Operación completada exitosamente'
    };
  }, []);

  const resetearFixturesDesarrollo = useCallback(async () => {
    cupsService.resetearFixtures();
    await cargarServicios();
  }, [cargarServicios]);

  const debeNotificarGestionCompleta = useCallback((): boolean => {
    const estado = estadoSesion.current;
    const progreso = progresoGestion;
    
    const resultado = (
      estado.pendientesIniciales !== null &&
      estado.pendientesIniciales > 0 &&
      estado.tuvoOperacionesExitosas &&
      !yaNotificadoRef.current &&
      progreso?.TotalPendientes === 0 &&
      progreso?.TotalCups > 0
    );
    
    console.log('🔍 [Hook] Verificación notificación:', {
      pendientesIniciales: estado.pendientesIniciales,
      tuvoOperacionesExitosas: estado.tuvoOperacionesExitosas,
      yaNotificado: yaNotificadoRef.current,
      totalPendientes: progreso?.TotalPendientes,
      totalCups: progreso?.TotalCups,
      resultado
    });
    
    return resultado;
  }, [progresoGestion]);

  const notificarGestionCompleta = useCallback(async (
    anexoId: number,
    onMostrarResultado?: (
      tipo: 'aprobado' | 'rechazado', 
      porcentaje: number, 
      totalRechazados: number,
      correoDestino?: string,
      estadoNotificacion?: string,
      mensajeNotificacion?: string
    ) => void,
    correoOverride?: string
  ) => {
    yaNotificadoRef.current = true;
    console.log('🎯 [Hook] Enviando notificación de gestión completa para anexo:', anexoId);
    
    // Obtener totales del progreso actual
    const totalCups = progresoGestion?.TotalCups || 0;
    const totalRechazados = progresoGestion?.TotalRechazados || 0;
    
    await cupsService.notificarGestionCompleta(
      anexoId,
      totalCups,
      totalRechazados,
      nombreAnexoCorreo,
      codigoAnexoCorreo,
      onMostrarResultado,
      correoOverride
    );
  }, [progresoGestion, nombreAnexoCorreo, codigoAnexoCorreo]);


  // Efecto para monitorear cuando se completa la gestión durante la sesión
  useEffect(() => {
    if (debeNotificarGestionCompleta() && anexoId) {
      console.log('🎉 [Hook] ¡Gestión completa detectada! Verificando correo del aprobador...');

      const aprobadorInfoStr = typeof localStorage !== 'undefined' ? localStorage.getItem('aprobador-info') : null;
      let correoAprobador = '';
      try {
        if (aprobadorInfoStr) {
          const aprobador = JSON.parse(aprobadorInfoStr);
          correoAprobador = (aprobador?.correo || '').trim();
        }
      } catch (e) {
        console.error('Error al parsear aprobador-info:', e);
      }

      const correoEsValido = esCorreoValido(correoAprobador);

      if (correoEsValido || !FEATURE_NOTIFICACION_CORREO_HABILITADA) {
        console.log(`📧 [Hook] Correo válido detectado en sesión (${correoAprobador}). Notificando directamente...`);
        notificarGestionCompleta(anexoId, onMostrarResultadoGestion).catch(error => {
          console.error('Error al notificar gestión completa del anexo:', error);
        });
      } else {
        console.warn('⚠️ [Hook] Correo del aprobador vacío o inválido. Solicitando modal de correo...');
        if (onSolicitarCorreoAprobador) {
          onSolicitarCorreoAprobador(anexoId, async (correoConfirmado: string) => {
            console.log(`📧 [Hook] Correo confirmado en modal (${correoConfirmado}). Notificando...`);
            await notificarGestionCompleta(anexoId, onMostrarResultadoGestion, correoConfirmado);
          });
        } else {
          notificarGestionCompleta(anexoId, onMostrarResultadoGestion).catch(error => {
            console.error('Error al notificar gestión completa del anexo:', error);
          });
        }
      }
    }
  }, [progresoGestion, anexoId, debeNotificarGestionCompleta, notificarGestionCompleta, onMostrarResultadoGestion, onSolicitarCorreoAprobador]);


  const actualizarEstadoAnexo = useCallback(async (estado: "0" | "5", observaciones?: string) => {
    if (!anexoId) {
      throw new Error('No hay anexo seleccionado');
    }

    setProcesando(true);
    try {
      return await cupsService.actualizarEstadoAnexo(
        anexoId,
        estado,
        observaciones,
        undefined,
        nombreAnexoCorreo,
        codigoAnexoCorreo
      );
    } finally {
      setProcesando(false);
    }
  }, [anexoId, nombreAnexoCorreo, codigoAnexoCorreo]);

  return {
    servicios,
    progresoGestion,
    loading,
    procesando,
    cargarServicios,
    actualizarEstadoCups,
    verificarCupsPendientes,
    validarRespuestaServicio,
    resetearFixturesDesarrollo,
    notificarGestionCompleta,
    debeNotificarGestionCompleta,
    programarRecargaDiferida,
    actualizarEstadoAnexo
  };
};